import React from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Stack,
  Tooltip,
  Dot,
  Icon,
  P,
  Popover,
  Avatar,
  getDateTime,
  Button,
  Feedback,
} from '@fashionnovateam/nova-ui-toolkit';
import cn from 'classnames';
import {
  PurchaseOrder,
  BulkProdSubStage,
} from '../../Lib/Types/purchase-order';
import PoStatus from '../../Lib/Constants/purchase-order-status';
import getPoStatus from '../../Lib/Utils/Helpers/make-button-text';
import { PoStatusComponent } from './PoStatus';
import { formatDate, formatDolar } from './utils';
import { ActionButtonPopover } from './PoCardButtons';
import { DiffHighlight } from '../ReusableComponents/NewDesign/DiffHighlight/DiffHighlight';
import { User } from '../../Lib/Api/vendor';
import { useMarkTOPShipped } from '../../Lib/Hooks/purchase-orders.hooks';
import { useNavigateToAsn } from '../../Lib/Hooks/ASN';
import { formatAsnLastEdited } from '../../Lib/Utils/Asn/getAsnLastEditedDate';
import { isPoAsnEligible } from '../../Lib/Utils/Asn/isPoAsnEligible';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';

type PoCardInfoProps = {
  purchaseOrder: PurchaseOrder;
  userId: string;
  details?: boolean;
  handleAcceptModal?: () => void;
  handleRejectModal?: () => void;
  handlePrintUnitLabelModal?: () => void;
  handlePrintHangTagModal?: () => void;
  handleOpenRevisionDrawer?: () => void;
  onPoSubStageUpdate?: (sub_stage: BulkProdSubStage) => void;
  perms: {
    canAccept: boolean;
    canReject: boolean;
    canPrint: boolean;
    canUpdateBulkStatus: boolean;
    canViewCost: boolean;
    canCreateRevision: boolean;
  };
  user: User;
  onProp65DrawerOpen?: () => void;
};

type PoCardInfoContentProps = {
  purchaseOrder: PurchaseOrder;
  details?: boolean;
  hasVendorAccepted: boolean;
  isRevision?: boolean;
  perms: {
    canViewCost: boolean;
  };
};

export function PoCardDetailsActions({
  purchaseOrder,
  userId,
  handleAcceptModal,
  handleRejectModal,
  handlePrintUnitLabelModal,
  handlePrintHangTagModal,
  handleOpenRevisionDrawer,
  onPoSubStageUpdate,
  perms,
  user,
}: PoCardInfoProps) {
  const { mutate: markTOPShippedMutate } = useMarkTOPShipped();

  return (
    <Stack gap="sm" direction="row">
      <PoStatusComponent
        details
        perms={perms}
        purchaseOrder={purchaseOrder}
        handleAcceptModal={handleAcceptModal}
        onPoSubStageUpdate={onPoSubStageUpdate}
      />
      {purchaseOrder.stage === 'TOP' &&
      purchaseOrder.sub_stage === 'TOP: Sample Pending Receipt' &&
      !purchaseOrder.is_cancelled ? (
        <Button
          onClick={() => {
            markTOPShippedMutate({
              userId,
              purchaseOrderId: purchaseOrder.po_number,
            });
          }}
        >
          Mark TOP as Shipped
        </Button>
      ) : null}
      <Popover position="bottom-right" offset={8}>
        <Tooltip content="Actions" position="top" arrow>
          <Popover.Button
            size="sm"
            color="secondary"
            className="shadow-none text-[#1D2433CC]"
          >
            Actions <Icon name="chevron/chevron-down" />
          </Popover.Button>
        </Tooltip>
        <Popover.Content>
          <ActionButtonPopover
            purchaseOrder={purchaseOrder}
            userId={userId}
            handleAcceptModal={handleAcceptModal}
            handleRejectModal={handleRejectModal}
            handlePrintUnitLabelModal={handlePrintUnitLabelModal}
            handlePrintHangTagModal={handlePrintHangTagModal}
            handleOpenRevisionDrawer={handleOpenRevisionDrawer}
            perms={perms}
            user={user}
          />
        </Popover.Content>
      </Popover>
    </Stack>
  );
}

const getTotal = (purchaseOrder: PurchaseOrder) =>
  formatDolar(
    purchaseOrder.purchase_order_lines?.reduce((acc, item) => {
      if (item.item_name?.includes('Subtotal')) return acc;
      if ((item.vendor_style === '-' || !item.vendor_style) && item.total) {
        return acc + item.total;
      }
      return (
        (item.price && item.quantity ? item.price * item.quantity : 0) + acc
      );
    }, 0) || 0,
  );

const getOldTotal = (purchaseOrder: PurchaseOrder) =>
  formatDolar(
    purchaseOrder.purchase_order_lines?.reduce((acc, item) => {
      if (item.item_name?.includes('Subtotal')) return acc;
      if ((item.vendor_style === '-' || !item.vendor_style) && item.total) {
        return acc + item.total;
      }
      const revisedPrice = item?.revision_log_line?.find(
        (revision) => revision.field_name === 'price',
      );
      const revisiedQuantity = item?.revision_log_line?.find(
        (revision) => revision.field_name === 'quantity',
      );
      const price = revisedPrice?.old_value
        ? parseFloat(revisedPrice?.old_value)
        : item.price;
      const quantity =
        revisiedQuantity?.old_value != null
          ? parseInt(revisiedQuantity?.old_value, 10)
          : item.quantity;
      const lineTotal = price && quantity ? price * quantity : 0;
      return (lineTotal || 0) + acc;
    }, 0) || 0,
  );

const hasRevision = (purchaseOrder: PurchaseOrder) =>
  (purchaseOrder.revision_log_header &&
    purchaseOrder.revision_log_header.length > 0) ||
  purchaseOrder.purchase_order_lines?.some(
    (item) => item.revision_log_line && item.revision_log_line.length > 0,
  );

function PoCardInfoContent({
  purchaseOrder,
  details,
  hasVendorAccepted,
  isRevision = false,
  perms,
}: PoCardInfoContentProps) {
  /* Get quantity and revised quantity if any */
  const qty = purchaseOrder.purchase_order_lines?.reduce(
    (acc, item) => (item?.quantity || 0) + acc,
    0,
  );
  const oldQty = purchaseOrder.purchase_order_lines?.reduce((acc, item) => {
    const qtyRevision = item?.revision_log_line?.find(
      (revision) => revision.field_name === 'quantity',
    );
    return (
      (qtyRevision?.old_value != null
        ? parseInt(qtyRevision?.old_value, 10)
        : item.quantity || 0) + acc
    );
  }, 0);

  /* Get total and revised total if any */
  const total = getTotal(purchaseOrder);
  const oldTotal = getOldTotal(purchaseOrder);

  const isFca = purchaseOrder.incoterm === 'FCA';

  /* Get due date and revised due date if any */
  const due =
    isFca && purchaseOrder.fob_ship_date
      ? getDateTime(purchaseOrder.fob_ship_date, false, 'UTC')
      : getDateTime(purchaseOrder.due_date, false, 'UTC');
  const oldDue = purchaseOrder.revision_log_header?.find((revision) =>
    isFca && purchaseOrder.fob_ship_date
      ? revision.field_name === 'fob_ship_date'
      : revision.field_name === 'due_date',
  );
  const formattedOldDue = oldDue
    ? getDateTime(oldDue.old_value, false, 'UTC')
    : null;

  const originalDueDate =
    purchaseOrder.original_due_date &&
    purchaseOrder.original_due_date !== purchaseOrder.due_date
      ? formatDate(purchaseOrder.original_due_date)
      : undefined;
  const originalFobDueDate =
    purchaseOrder.original_fob_ship_date &&
    purchaseOrder.original_fob_ship_date !== purchaseOrder.fob_ship_date
      ? formatDate(purchaseOrder.original_fob_ship_date)
      : undefined;
  const topDueDate = getDateTime(purchaseOrder.top_due_date, false, 'UTC');
  const delivery = `${purchaseOrder.shipment_mode} •`;
  const deliveryReference = purchaseOrder.delivery?.booking.reference;
  const deliveryDate = purchaseOrder.delivery
    ? formatDate(purchaseOrder.delivery.booking.starts_at, 'delivery', 'UTC')
    : '-';
  const deliveredAtDate = purchaseOrder.delivery?.delivered_at
    ? formatDate(purchaseOrder.delivery.delivered_at, 'delivery', 'UTC')
    : undefined;

  return (
    <Stack
      direction="col"
      className={cn(details ? 'gap-[6px]' : 'gap-[3px]', '*:whitespace-nowrap')}
    >
      {details && (
        <Stack
          gap="xs"
          direction="row"
          align="center"
          className="text-base-disabled text-xsm"
        >
          <Avatar
            name={purchaseOrder.buyer_name}
            size="xs"
            className="static"
          />
          <span>{purchaseOrder.buyer_name}</span>
        </Stack>
      )}
      <Stack
        gap="xs"
        direction="row"
        align="center"
        className="text-base-disabled text-xsm"
      >
        {details && <Icon name="package/package-01" />}
        <span className="flex">
          <span className="mr-1">Qty:</span>
          {isRevision && qty !== oldQty ? (
            <DiffHighlight
              newValue={`${qty}`}
              oldValue={`${oldQty}`}
              variant={hasVendorAccepted ? 'secondary' : 'primary'}
            />
          ) : (
            qty || 0
          )}
        </span>
      </Stack>
      <Stack
        gap="xs"
        direction="row"
        align="center"
        className="text-base-disabled text-xsm"
      >
        {details && <Icon name="timer/timer-check" />}
        <span className="flex">
          <span className="mr-1">{isFca ? 'FOB Ship' : 'Due'}:</span>
          {isRevision && formattedOldDue ? (
            <DiffHighlight
              newValue={`${due}`}
              oldValue={`${formattedOldDue}`}
              variant={hasVendorAccepted ? 'secondary' : 'primary'}
            />
          ) : (
            due
          )}
        </span>
        {(!isFca && originalDueDate) || (isFca && originalFobDueDate) ? (
          <Tooltip
            content={`Original due date: ${isFca ? originalFobDueDate : originalDueDate}`}
            position="top"
            className="max-w-[115px]"
            arrow
          >
            <Dot color="red" />
          </Tooltip>
        ) : null}
      </Stack>
      {perms.canViewCost && (
        <Stack
          gap="xs"
          direction="row"
          align="center"
          className="text-base-disabled text-xsm"
        >
          {details && <Icon name="credit-card/credit-card" />}
          <Tooltip
            content="Total = Total Value of the items purchase"
            position="top"
            arrow
          >
            <span className="mr-1">Total:</span>
            {isRevision && total !== oldTotal ? (
              <>
                <span
                  className={`whitespace-nowrap ${hasVendorAccepted ? 'text-base-primary font-semibold' : 'text-warning-900 bg-warning-100'}`}
                >
                  {total}{' '}
                </span>
                <Badge
                  size="sm"
                  contrast="low"
                  color={hasVendorAccepted ? 'neutral' : 'yellow'}
                  className={`ml-1 ${hasVendorAccepted ? 'font-semibold' : 'text-warning-900'}`}
                >
                  {purchaseOrder.incoterm}
                </Badge>
                <span
                  className={`line-through ml-2 ${hasVendorAccepted ? 'text-base-disabled opacity-55' : 'text-warning-900'}`}
                >
                  {oldTotal}
                </span>
                <span
                  className={`line-through text-xs ml-1 ${hasVendorAccepted ? 'text-base-disabled opacity-55' : 'text-warning-900'}`}
                >
                  {purchaseOrder.incoterm}
                </span>
              </>
            ) : (
              <>
                <span>{total}</span>
                {purchaseOrder.incoterm && (
                  <Badge size="sm" contrast="low">
                    {purchaseOrder.incoterm}
                  </Badge>
                )}
              </>
            )}
          </Tooltip>
        </Stack>
      )}
      {(purchaseOrder.purchase_type === 'New' ||
        purchaseOrder?.top_required) && (
        <Stack
          gap="xs"
          direction="row"
          align="center"
          className="text-base-disabled text-xsm"
        >
          {details && <Icon name="scale" />}
          <span>TOP: {topDueDate}</span>
        </Stack>
      )}
      <Stack
        gap="xs"
        direction="row"
        align="center"
        className="text-base-disabled text-xsm"
      >
        {details && <Icon name="truck/truck-trash" />}
        <span>Delivery: {purchaseOrder.shipment_mode ? delivery : ''}</span>
        {deliveryReference ? (
          <Tooltip
            content={
              deliveredAtDate
                ? `Delivered on ${deliveredAtDate}`
                : `Delivery scheduled for ${deliveryDate}`
            }
            position="top"
            className="max-w-[160px]"
            arrow
          >
            {deliveryReference}
          </Tooltip>
        ) : (
          'TBD'
        )}
      </Stack>
      {purchaseOrder.division && (
        <Stack
          gap="xs"
          direction="row"
          align="center"
          className="text-base-disabled text-xsm"
        >
          {details && <Icon name="layer/layer-three" />}
          <span>Division: {purchaseOrder.division}</span>
        </Stack>
      )}
    </Stack>
  );
}

function PoRevisionInfoMessage({
  isRevision,
  hasVendorAccepted,
  canAccept,
}: {
  isRevision: boolean;
  hasVendorAccepted: boolean;
  canAccept: boolean;
}) {
  if (!(isRevision && !hasVendorAccepted)) return null;
  if (!canAccept) {
    return (
      <P className="text-base-disabled text-sm">
        Note: To access printing features, the revisions on this PO must be
        accepted by an authorized user at your company.
      </P>
    );
  }
  return (
    <P className="text-base-disabled text-sm">
      Please review and accept all of the changes{' '}
      <span className="text-warning-900">listed in orange</span> below. If you
      have any questions, please contact us.
    </P>
  );
}

export function PoCardInfo({
  purchaseOrder,
  userId,
  details,
  handleAcceptModal,
  handleRejectModal,
  handlePrintUnitLabelModal,
  handlePrintHangTagModal,
  handleOpenRevisionDrawer,
  onPoSubStageUpdate,
  perms,
  user,
  onProp65DrawerOpen,
}: PoCardInfoProps) {
  const { flags } = React.useContext(FeatureFlagsContext);

  const isAsnEligible =
    !!details && isPoAsnEligible(purchaseOrder, !!flags.generate_asn?.enabled);

  const {
    navigateToAsn,
    isLoading: isAsnLoading,
    isGenerating,
    asnData,
  } = useNavigateToAsn(userId, purchaseOrder.po_number, isAsnEligible);
  const isRevision = React.useMemo(
    () => hasRevision(purchaseOrder),
    [purchaseOrder],
  );

  const isProp65Completed = React.useMemo(
    () =>
      purchaseOrder.purchase_order_lines.every(
        (line) => line.prop65 !== null && line.prop65 !== undefined,
      ),
    [purchaseOrder],
  );

  const kidsDivision = React.useMemo(
    () => purchaseOrder.division === "KID'S",
    [purchaseOrder],
  );

  const lastAsnEditedDate = React.useMemo(
    () => (asnData ? formatAsnLastEdited(asnData) : 'Never'),
    [asnData],
  );

  const acceptButtonText = getPoStatus({
    revised_at: purchaseOrder.revised_at,
    vendor_rejection_reason: purchaseOrder.vendor_rejection_reason,
    revision_accepted_at: purchaseOrder.revision_accepted_at,
  });

  const hasVendorAccepted =
    acceptButtonText === PoStatus.ACCEPTED ||
    acceptButtonText === PoStatus.PENDING;

  return (
    <Stack gap="md" direction="col" className="pt-4 pb-3 flex-1">
      <Stack direction="col" className="gap-[6px]">
        {details ? (
          <P size="2xl" color="primary" weight="semibold">
            {purchaseOrder.po_number}
          </P>
        ) : (
          <Link
            className="w-full"
            to={`purchase-orders/${purchaseOrder.po_number}`}
          >
            <span className="text-base-primary font-semibold text-md">
              {purchaseOrder.po_number}
            </span>
          </Link>
        )}
        {details ? (
          <PoRevisionInfoMessage
            isRevision={isRevision}
            hasVendorAccepted={hasVendorAccepted}
            canAccept={perms.canAccept}
          />
        ) : null}
        {!details ? (
          <PoStatusComponent purchaseOrder={purchaseOrder} />
        ) : (
          <PoCardDetailsActions
            purchaseOrder={purchaseOrder}
            userId={userId}
            handleAcceptModal={handleAcceptModal}
            handleRejectModal={handleRejectModal}
            handlePrintUnitLabelModal={handlePrintUnitLabelModal}
            handlePrintHangTagModal={handlePrintHangTagModal}
            handleOpenRevisionDrawer={handleOpenRevisionDrawer}
            onPoSubStageUpdate={onPoSubStageUpdate}
            perms={perms}
            user={user}
          />
        )}
        {isAsnEligible && (
          <div data-testid="generate-asn-banner">
            <Feedback
              type="info"
              iconName="file-02/file-02-plus"
              title="You're ready to create an ASN for this order."
              description={`Last edited: ${lastAsnEditedDate}`}
              className="w-[554px]"
            >
              <Button
                color="primary"
                loading={isGenerating}
                onClick={navigateToAsn}
                disabled={isAsnLoading}
                data-testid="generate-asn-banner-button"
              >
                Generate ASN
              </Button>
            </Feedback>
          </div>
        )}
        {flags.prop65 && flags.prop65.enabled && details && kidsDivision && (
          <Feedback
            type="warning"
            title="Fashion Nova does NOT accept any children's products that require Prop 65 warning. "
            className="w-[533px]"
          />
        )}
        {flags.prop65 &&
          flags.prop65.enabled &&
          details &&
          !isProp65Completed &&
          !kidsDivision && (
            <Feedback
              type="info"
              title="Complete Proposition 65 Compliance"
              description="Indicate which SKUs require a Prop 65 warning."
              className="w-[533px]"
            >
              <Button color="secondary" onClick={onProp65DrawerOpen}>
                Set CA Prop 65
              </Button>
            </Feedback>
          )}
      </Stack>
      {details ? (
        <PoCardInfoContent
          purchaseOrder={purchaseOrder}
          details={details}
          isRevision={isRevision}
          hasVendorAccepted={hasVendorAccepted}
          perms={perms}
        />
      ) : (
        <Link
          className="w-full"
          to={`purchase-orders/${purchaseOrder.po_number}`}
        >
          <PoCardInfoContent
            purchaseOrder={purchaseOrder}
            details={details}
            hasVendorAccepted={hasVendorAccepted}
            perms={perms}
          />
        </Link>
      )}
    </Stack>
  );
}
